// Comprehensive logging system for FactoryGPT
import { useEffect, useState } from 'react';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export enum LoggerType {
  WORLD_GENERATION = 'world-generation',
  GAME_SAVING = 'game-saving',
  PLAYER_MOVEMENT = 'player-movement',
  BUILDING_SYSTEM = 'building-system',
  BELT_SYSTEM = 'belt-system',
  RESOURCE_MANAGEMENT = 'resource-management',
  INPUT_HANDLING = 'input-handling',
  COLLISION_SYSTEM = 'collision-system',
  GENERAL = 'general',
}

// Type for log data - allows objects, arrays, primitives, or undefined
type LogData =
  | Record<string, unknown>
  | unknown[]
  | string
  | number
  | boolean
  | undefined;

interface LogEntry {
  timestamp: number;
  level: LogLevel;
  type: LoggerType;
  message: string;
  data?: LogData;
}

// LocalStorage key for logger settings
const LOGGER_SETTINGS_KEY = 'automation-game-logger-settings';

interface LoggerSettings {
  enabledLoggers: LoggerType[];
  logLevel: LogLevel;
}

class LoggerSystem {
  private enabledLoggers: Set<LoggerType> = new Set();
  private logLevel: LogLevel = LogLevel.INFO;
  private logHistory: LogEntry[] = [];
  private maxHistorySize: number = 1000;
  private changeCallbacks: Set<() => void> = new Set();

  constructor() {
    this.loadSettings();
  }

  // Load settings from localStorage
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem(LOGGER_SETTINGS_KEY);
      if (saved) {
        const settings: LoggerSettings = JSON.parse(saved);
        this.enabledLoggers = new Set(settings.enabledLoggers);
        this.logLevel = settings.logLevel;
        return;
      }
    } catch {
      // If loading fails, use defaults
    }

    // Default settings
    if (process.env.NODE_ENV === 'development') {
      Object.values(LoggerType).forEach(type => {
        this.enabledLoggers.add(type);
      });
      this.logLevel = LogLevel.DEBUG;
    } else {
      // In production, only enable essential loggers
      this.enabledLoggers.add(LoggerType.GENERAL);
      this.logLevel = LogLevel.WARN;
    }
    this.saveSettings();
  }

  // Save settings to localStorage
  private saveSettings(): void {
    try {
      const settings: LoggerSettings = {
        enabledLoggers: Array.from(this.enabledLoggers),
        logLevel: this.logLevel,
      };
      localStorage.setItem(LOGGER_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Ignore localStorage errors
    }
  }

  // Register callback for when logger settings change
  onChange(callback: () => void): () => void {
    this.changeCallbacks.add(callback);
    return () => this.changeCallbacks.delete(callback);
  }

  // Notify all registered callbacks of changes
  private notifyChange(): void {
    this.changeCallbacks.forEach(callback => callback());
  }

  // Enable/disable specific logger types
  enableLogger(type: LoggerType): void {
    this.enabledLoggers.add(type);
    this.saveSettings();
    this.notifyChange();
  }

  disableLogger(type: LoggerType): void {
    this.enabledLoggers.delete(type);
    this.saveSettings();
    this.notifyChange();
  }

  isLoggerEnabled(type: LoggerType): boolean {
    return this.enabledLoggers.has(type);
  }

  getEnabledLoggers(): LoggerType[] {
    return Array.from(this.enabledLoggers);
  }

  // Set minimum log level
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.saveSettings();
    this.notifyChange();
  }

  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  // Core logging method
  private log(
    level: LogLevel,
    type: LoggerType,
    message: string,
    data?: LogData
  ): void {
    // Check if logger type is enabled and level meets threshold
    if (!this.enabledLoggers.has(type) || level < this.logLevel) {
      return;
    }

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      type,
      message,
      data,
    };

    // Add to history
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    // Output to console with appropriate styling
    this.outputToConsole(entry);
  }

  private outputToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const levelName = LogLevel[entry.level];
    const prefix = `[${timestamp}] [${levelName}] [${entry.type}]`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console
        console.debug(
          `%c${prefix}%c ${entry.message}`,
          'color: #888',
          'color: inherit',
          entry.data
        );
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(
          `%c${prefix}%c ${entry.message}`,
          'color: #4CAF50',
          'color: inherit',
          entry.data
        );
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(
          `%c${prefix}%c ${entry.message}`,
          'color: #FF9800',
          'color: inherit',
          entry.data
        );
        break;
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(
          `%c${prefix}%c ${entry.message}`,
          'color: #F44336',
          'color: inherit',
          entry.data,
          typeof entry.data === 'object' &&
            entry.data !== null &&
            !Array.isArray(entry.data) &&
            'error' in entry.data
            ? entry.data.error
            : undefined
        );
        break;
    }
  }

  // Public logging methods
  debug(type: LoggerType, message: string, data?: LogData): void {
    this.log(LogLevel.DEBUG, type, message, data);
  }

  info(type: LoggerType, message: string, data?: LogData): void {
    this.log(LogLevel.INFO, type, message, data);
  }

  warn(type: LoggerType, message: string, data?: LogData): void {
    this.log(LogLevel.WARN, type, message, data);
  }

  error(type: LoggerType, message: string, data?: LogData): void {
    this.log(LogLevel.ERROR, type, message, data);
  }

  // Get log history for debug menu
  getLogHistory(): LogEntry[] {
    return [...this.logHistory];
  }

  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logHistory.slice(-count);
  }

  clearHistory(): void {
    this.logHistory = [];
  }

  // Get logger statistics
  getLoggerStats(): Record<LoggerType, number> {
    const stats: Record<LoggerType, number> = {} as Record<LoggerType, number>;

    Object.values(LoggerType).forEach(type => {
      stats[type] = this.logHistory.filter(entry => entry.type === type).length;
    });

    return stats;
  }
}

// Global logger instance
export const logger = new LoggerSystem();

// Initialize with a test message to verify the logger is working
logger.info(LoggerType.GENERAL, 'Logger system initialized', {
  enabledLoggers: logger.getEnabledLoggers().length,
  logLevel: LogLevel[logger.getLogLevel()],
});

// Convenience functions for specific logger types
export const worldLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.WORLD_GENERATION, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.WORLD_GENERATION, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.WORLD_GENERATION, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.WORLD_GENERATION, message, data),
};

export const saveLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.GAME_SAVING, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.GAME_SAVING, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.GAME_SAVING, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.GAME_SAVING, message, data),
};

export const movementLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.PLAYER_MOVEMENT, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.PLAYER_MOVEMENT, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.PLAYER_MOVEMENT, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.PLAYER_MOVEMENT, message, data),
};

export const buildingLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.BUILDING_SYSTEM, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.BUILDING_SYSTEM, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.BUILDING_SYSTEM, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.BUILDING_SYSTEM, message, data),
};

export const resourceLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.RESOURCE_MANAGEMENT, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.RESOURCE_MANAGEMENT, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.RESOURCE_MANAGEMENT, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.RESOURCE_MANAGEMENT, message, data),
};

export const inputLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.INPUT_HANDLING, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.INPUT_HANDLING, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.INPUT_HANDLING, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.INPUT_HANDLING, message, data),
};

export const beltLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.BELT_SYSTEM, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.BELT_SYSTEM, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.BELT_SYSTEM, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.BELT_SYSTEM, message, data),
};

export const generalLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.GENERAL, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.GENERAL, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.GENERAL, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.GENERAL, message, data),
};

export const collisionLogger = {
  debug: (message: string, data?: LogData) =>
    logger.debug(LoggerType.COLLISION_SYSTEM, message, data),
  info: (message: string, data?: LogData) =>
    logger.info(LoggerType.COLLISION_SYSTEM, message, data),
  warn: (message: string, data?: LogData) =>
    logger.warn(LoggerType.COLLISION_SYSTEM, message, data),
  error: (message: string, data?: LogData) =>
    logger.error(LoggerType.COLLISION_SYSTEM, message, data),
  isEnabled: () => logger.isLoggerEnabled(LoggerType.COLLISION_SYSTEM),
};

// React hook for logger state management
export interface UseLoggerReturn {
  enabledLoggers: LoggerType[];
  logLevel: LogLevel;
  enableLogger: (type: LoggerType) => void;
  disableLogger: (type: LoggerType) => void;
  setLogLevel: (level: LogLevel) => void;
  isLoggerEnabled: (type: LoggerType) => boolean;
  clearHistory: () => void;
  getLogStats: () => Record<LoggerType, number>;
}

export const useLogger = (): UseLoggerReturn => {
  const [state, setState] = useState({
    enabledLoggers: logger.getEnabledLoggers(),
    logLevel: logger.getLogLevel(),
  });

  useEffect(() => {
    // Subscribe to logger changes
    const unsubscribe = logger.onChange(() => {
      setState({
        enabledLoggers: logger.getEnabledLoggers(),
        logLevel: logger.getLogLevel(),
      });
    });

    return unsubscribe;
  }, []);

  return {
    enabledLoggers: state.enabledLoggers,
    logLevel: state.logLevel,
    enableLogger: (type: LoggerType) => logger.enableLogger(type),
    disableLogger: (type: LoggerType) => logger.disableLogger(type),
    setLogLevel: (level: LogLevel) => logger.setLogLevel(level),
    isLoggerEnabled: (type: LoggerType) => logger.isLoggerEnabled(type),
    clearHistory: () => logger.clearHistory(),
    getLogStats: () => logger.getLoggerStats(),
  };
};
